/**
 * Icons for the "how it is done today" column — the manual habit Chickrah
 * replaces.
 *
 * The reference layout this follows puts recognisable product logos in this
 * column. Ours are not products, they are paper and memory, so each one is
 * drawn instead: a spreadsheet grid, a notebook, tally marks, footprints.
 *
 * Same 24x24 grid and 1.7 stroke as ComparisonIcon so the two columns sit at
 * matching weight. WhatsApp is the exception — it is a real product, and its
 * glyph is a solid mark rather than a line drawing, so it renders filled.
 */
const FILLED = new Set(['whatsapp']);

const PATHS = {
  /*
   * Eye — someone has to physically go and look.
   *
   * This was a pair of footprints first. At the 14px the mobile cards render
   * it at, two offset ellipses read as a percent sign, not as steps. An eye
   * survives being small, which is the only size that matters here.
   */
  steps: (
    <>
      <path d="M2.6 12S6.2 5.6 12 5.6 21.4 12 21.4 12 17.8 18.4 12 18.4 2.6 12 2.6 12Z" />
      <circle cx="12" cy="12" r="2.7" />
    </>
  ),

  // page with ruled lines — paper log
  paper: (
    <>
      <path d="M13.8 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8.2l-4.7-4.7Z" />
      <path d="M13.8 3.5v4.7h4.7" />
      <path d="M8.6 12.6h6.8M8.6 16h4.8" />
    </>
  ),

  // ruled grid — spreadsheet
  spreadsheet: (
    <>
      <rect x="3.6" y="4.6" width="16.8" height="14.8" rx="2" />
      <path d="M3.6 9.4h16.8M9.6 4.6v14.8M15 4.6v14.8" />
    </>
  ),

  // four strokes strike-through — hand tally
  tally: (
    <>
      <path d="M6.4 6v12M10.1 6v12M13.8 6v12M17.5 6v12" />
      <path d="M4.8 17.4L19.2 6.6" />
    </>
  ),

  // bound book — the notebook on the desk
  notebook: (
    <>
      <path d="M6.6 3.6h10.9a1.5 1.5 0 0 1 1.5 1.5v13.8a1.5 1.5 0 0 1-1.5 1.5H6.6a2 2 0 0 1-2-2V5.6a2 2 0 0 1 2-2Z" />
      <path d="M8.6 3.6v16.8" />
      <path d="M11.6 8.4h4.6M11.6 12h4.6" />
    </>
  ),

  // clock — a check that only happens now and then
  clock: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.4V12l3.1 1.9" />
    </>
  ),

  // WhatsApp — a real product, so the real glyph
  whatsapp: (
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  ),
};

export default function TodayIcon({ name, className = '' }) {
  const filled = FILLED.has(name);

  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : '1.7'}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name] ?? PATHS.paper}
    </svg>
  );
}
